import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  Popup,
  Button,
  Confirm,
  Image,
} from "semantic-ui-react";
import { ENV, buildStorageUrl } from "../../../../../../../utils";
import { AudioUploader } from "../../../../../../Shared";
import { useDropzone } from "react-dropzone";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Smu02 = React.memo((props) => {
  const { index, slideId, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [title, setTitle] = useState("");
  const [titleAudio, setTitleAudio] = useState("");
  const [image, setImage] = useState("");
  const [audio, setAudio] = useState(null);

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Updates the component state with the content of the currently selected slide.
   *
   * This `useEffect` hook is triggered whenever the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide that matches the `slideId`. If such a slide is found and it contains content, the hook
   * updates the component's state with the title, image, title audio, and audio from the first audio object
   * in the slide's content.
   *
   * This ensures that the component reflects the content of the current slide, specifically the first audio
   * object's details, allowing for accurate editing and presentation of slide information.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTitle(slide.content.title);
      setImage(slide.content.audios[0].image);
      setTitleAudio(slide.content.audios[0].title);
      setAudio(slide.content.audios[0].audio);
    }
  }, [slideId]);

  /**
   * Updates the title, image, title audio, and audio of the first audio object in a slide's content.
   *
   * This `useEffect` hook is triggered when there are changes to the `title`, `image`, `titleAudio`, or `audio` states.
   * It searches the `slidesActiveSessionEdit` array for a slide matching the `slideId`. If found, it updates the
   * slide's content with the new values for title, and the first audio object's image, title, and audio.
   *
   * This ensures that changes made to these properties are reflected in the active session's slide array, keeping
   * the session data up-to-date with the user's edits.
   *
   * @dependency title - The new title for the slide and the first audio object.
   * @dependency image - The new image for the first audio object.
   * @dependency titleAudio - The new title for the first audio object.
   * @dependency audio - The new audio file for the first audio object.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.title = title;
      slidesActiveSessionEdit[slideIndex].content.audios[0].image = image;
      slidesActiveSessionEdit[slideIndex].content.audios[0].title = titleAudio;
      slidesActiveSessionEdit[slideIndex].content.audios[0].audio = audio;
    }
  }, [title, image, titleAudio, audio]);

  /**
   * Event handlers and configurations for updating slide title, title audio, and handling image drops.
   *
   * `handleTitleChange`:
   * - Listens for changes in the title input field.
   * - Updates the `title` state, removing leading whitespace from the input value.
   *
   * `handleTitleAudioChange`:
   * - Listens for changes in the title audio input field.
   * - Updates the `titleAudio` state, also removing leading whitespace from the input value.
   *
   * `onDrop`:
   * - A callback for the dropzone that handles dropped files.
   * - Accepts the first file from the dropped files array and updates the `image` state with a URL representing the file.
   *
   * `useDropzone` configuration:
   * - Specifies that only JPEG and PNG images are acceptable for dropping.
   * - Utilizes the `onDrop` callback to handle file drops.
   *
   * These functions and configurations are part of the component's logic to manage slide content, including the title, title audio, and images.
   */
  const handleTitleChange = (event) => {
    setTitle(event.target.value.trimStart());
  };

  const handleTitleAudioChange = (event) => {
    setTitleAudio(event.target.value.trimStart());
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(URL.createObjectURL(file));
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    onDrop,
  });

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__input">
          <div className="media-slide__label-div">
            <Icon name="bookmark" />
            <h5 className="media-slide__label">Título:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input-title"
            name="title"
            value={title}
            onChange={handleTitleChange}
          />
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="play circle outline" />
            <h5 className="media-slide__label">Contenido:</h5>
          </div>
          <div className="media-slide__image-audio" {...getRootProps()}>
            <input {...getInputProps()} />
            <Image
              src={image?.startsWith('blob:') ? image : (buildStorageUrl(image) || image)}
              rounded
              fluid
            />
          </div>
          <div className="media-slide__label-div">
            <h5 className="media-slide__label">Título del audio:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input"
            name="titleAudio"
            value={titleAudio}
            onChange={handleTitleAudioChange}
          />
          <AudioUploader audio={audio} setAudio={setAudio} />
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
